import { ChangeEvent, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information, Package } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@mint-lib/tw-components';

import { useDataFileRouteApi } from '../../hooks/useImportHubRouteApi.js';
import { useCreateTemplateMutation } from '../../queries/index.js';
import { Input } from '../atoms/Input.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';

type SaveTemplateDialogProps = {
  open: boolean;
  onSubmit: (isNewTemplate: boolean) => Promise<void>;
  /** Called before the template is saved and the import is submitted. */
  onSubmitStart: () => void;
  /** Called when saving the template fails; `onSubmit` is not called then. */
  onSubmitError: () => void;
  onClose: () => void;
};

export const SaveTemplateDialog = ({
  open,
  onSubmit,
  onSubmitStart,
  onSubmitError,
  onClose,
}: SaveTemplateDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const { dataFileId } = useDataFileRouteApi().useParams();

  const [isNewTemplate, setIsNewTemplate] = useState(true);
  const [templateName, setTemplateName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { mutateAsync: createTemplate } = useCreateTemplateMutation();

  const trimmedTemplateName = templateName.trim();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    onSubmitStart();
    try {
      if (isNewTemplate) {
        await createTemplate({
          name: trimmedTemplateName,
          tableDataFileId: dataFileId,
        });
      }
      await onSubmit(isNewTemplate);
    } catch (error) {
      onSubmitError();
      if (error.type === 'DuplicatedTemplateName') {
        setErrorMessage(t('Template name already exists.'));
      } else {
        enqueueSnackbar({
          title: t('Error'),
          subTitle: error.message,
          variant: 'error',
          removable: true,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTemplateNameChange = (name: string) => {
    setTemplateName(name);
    setErrorMessage('');
  };

  const isFormValid =
    (!isNewTemplate || (isNewTemplate && trimmedTemplateName)) && !errorMessage;

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="m">
          <DialogHeader>
            <DialogTitle>{t('Import options')}</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <div className="flex flex-col gap-4">
              <DialogDescription>
                {t(
                  'To save this configuration for future imports, create a new template, that helps you avoid the first steps (header selection and data mapping) for Data Import flow.',
                )}
              </DialogDescription>
              <RadioGroup
                value={isNewTemplate ? 'new' : 'none'}
                onValueChange={(value) => setIsNewTemplate(value === 'new')}
              >
                <Card
                  variant="outlined"
                  selected={isNewTemplate}
                  className="w-full"
                  contentClassName="p-3"
                >
                  <div className="flex items-center justify-between">
                    <Label
                      data-dd-action-name="Import options: save as template"
                      variant="bodyShort01"
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <RadioGroupItem value="new" />
                      {t('Save as template')}
                    </Label>
                    <Tooltip
                      description={t(
                        'By choosing this option you can automate future imports. Find the auto-generated email in Templates tab for easy access.',
                      )}
                      side="top"
                    >
                      <Chip variant="filled" size="small" color="azure">
                        <Information />
                        {t('Recommended')}
                      </Chip>
                    </Tooltip>
                  </div>
                  <div className="mt-2">
                    <Input
                      data-dd-action-name="Import options: template name"
                      label={t('Template name*')}
                      placeholder={t('Enter template name')}
                      value={templateName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleTemplateNameChange(e.target.value)
                      }
                      color={errorMessage ? 'error' : undefined}
                      helperText={errorMessage}
                      disabled={!isNewTemplate}
                      maxLength={64}
                      autoComplete="off"
                    />
                  </div>
                </Card>
                <Card
                  variant="outlined"
                  selected={!isNewTemplate}
                  className="w-full"
                  contentClassName="p-3"
                >
                  <Label
                    data-dd-action-name="Import options: no template saving"
                    variant="bodyShort01"
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <RadioGroupItem value="none" />
                    {t('No template saving')}
                  </Label>
                </Card>
              </RadioGroup>
            </div>
          </DialogContent>
          <DialogFooter>
            <Button
              data-dd-action-name="Import options: cancel"
              size="large"
              variant="text"
              onClick={onClose}
            >
              {t('Cancel')}
            </Button>
            <Button
              data-dd-action-name="Import options: import file"
              size="large"
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              <Package className="size-4" />
              {t('Import file')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
